import {
  AccordionDetails,
  AccordionSummary,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React, { useEffect } from "react";
import CustomAccordion from "../CustomAccordion";
import {
  ApproverContainer,
  MinuteInfoContainer,
  TitleContainer,
} from "./styles";

import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../../../context/auth";
import { getApprovalsByCommitteeSlotId } from "../../../../../services/slotApprovals.service";
import {
  Approver,
  GetApprovalsResponseData,
} from "../../../../../types/approvals";
import { MinuteSlot } from "../../../../../types/minuteSlot";
import ApproverCard, { ApprovalType } from "../../../../Common/ApproverCard";
import Actions from "../../../minutes/EditAction/Actions";
import FinalDeliberation from "../../../minutes/EditAction/FinalDeliberation";
import GeneralComment from "../../../minutes/EditAction/GeneralComment";
import { checkAccess } from "../../../utils";
import CaveatCard from "../../../../Common/CaveatCard";
import { format } from "date-fns";
import {
  getDateFormats,
  getDateTimeLocale,
} from "../../../../../utils/get-date-time-locale";

interface IMinuteDeliberationProps {
  minuteSlot: MinuteSlot | null | undefined;
}

interface LocationState {
  slotCommitteeId: string;
}

type ScreenAccess =
  | "canSeeActions"
  | "canSeeGeneralComment"
  | "canSeeDeliberations"
  | "canSeeVotes";

const MinuteDeliberationInfo = ({ minuteSlot }: IMinuteDeliberationProps) => {
  const classes = useStyles();
  const { slotCommitteeId } = useLocation<LocationState>().state;
  const { userAccess } = useAuth() as { [k: string]: boolean };
  const [approvals, setApprovals] = React.useState<Approver[]>([]);
  const [featuresAccess, setFeaturesAccess] = React.useState<
    { [k in ScreenAccess]: boolean } | undefined
  >();

  const approverVaultTypeMapper: { [k: string]: ApprovalType } = {
    A: "APPROVED",
    R: "REPPROVED",
    C: "COMMENT",
    AR: "APPROVED",
    RR: "ROUND_ROBIN_APPROVED",
  };

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const getApprovals = async () => {
      return await getApprovalsByCommitteeSlotId({
        committeeSlotId: slotCommitteeId,
      });
    };

    getApprovals().then((response: GetApprovalsResponseData) => {
      setApprovals(response.approvers);
    });
  }, []);

  useEffect(() => {
    const canSeeActions = checkAccess(userAccess, "viewActionsInSlotDetails");
    const canSeeGeneralComment = checkAccess(
      userAccess,
      "viewCommentsInSlotDetails"
    );
    const canSeeDeliberations = checkAccess(
      userAccess,
      "viewDeliberationInSlotDetails"
    );
    const canSeeVotes = checkAccess(userAccess, "viewVotesInSlotDetails");

    setFeaturesAccess({
      canSeeActions,
      canSeeGeneralComment,
      canSeeDeliberations,
      canSeeVotes,
    });
  }, [userAccess]);

  return (
    <MinuteInfoContainer>
      <TitleContainer>
        <Typography className={classes.title}>
          {t("slotDetails.minuteInformations")}
        </Typography>
      </TitleContainer>

      {featuresAccess?.canSeeGeneralComment && (
        <CustomAccordion aria-describedby="generalCommentSection">
          <AccordionSummary
            expandIcon={<KeyboardArrowDownIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography className={classes.subtitle}>
              {t("slotDetails.generalComments")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <GeneralComment
              minuteStatus={minuteSlot?.status}
              minuteSlotId={minuteSlot?.minuteSlotId}
              generalComment={minuteSlot?.generalComments}
              t={t}
              isEditMode={false}
            />
          </AccordionDetails>
        </CustomAccordion>
      )}

      {featuresAccess?.canSeeActions && (
        <CustomAccordion aria-describedby="actionsSection">
          <AccordionSummary
            expandIcon={<KeyboardArrowDownIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography className={classes.subtitle}>
              {t("slotDetails.actions")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Actions
              t={t}
              minuteStatus={minuteSlot?.status}
              minuteSlotId={minuteSlot?.minuteSlotId}
              actions={minuteSlot?.actions}
              isEditMode={false}
            />
          </AccordionDetails>
        </CustomAccordion>
      )}

      {featuresAccess?.canSeeDeliberations && (
        <CustomAccordion aria-describedby="deliberationSection">
          <AccordionSummary
            expandIcon={<KeyboardArrowDownIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography className={classes.subtitle}>
              {t("slotDetails.finalDeliberations")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FinalDeliberation
              t={t}
              minuteStatus={minuteSlot?.status}
              minuteSlotId={minuteSlot?.minuteSlotId}
              finalDeliberation={minuteSlot?.finalDeliberation}
              isEditMode={false}
            />
          </AccordionDetails>
        </CustomAccordion>
      )}

      {featuresAccess?.canSeeVotes && (
        <CustomAccordion aria-describedby="votesSection">
          <AccordionSummary
            expandIcon={<KeyboardArrowDownIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography className={classes.subtitle}>
              {t("slotDetails.votes")}
            </Typography>
          </AccordionSummary>
          <ApproverContainer>
            {approvals
              .filter((approval) => approval.memberName)
              .map((approval) => (
                <ApproverCard
                  approver={{
                    approvalId: approval.id,
                    type: approverVaultTypeMapper[approval.vote],
                    caveat: approval.resalvas ?? undefined,
                    memberName: approval.memberName ?? undefined,
                    roundRobinDate: approval.roudRobbinDate
                      ? format(
                          new Date(approval.roudRobbinDate),
                          getDateFormats(i18n.language),
                          { locale: getDateTimeLocale(i18n.language) }
                        )
                      : undefined,
                  }}
                  key={approval.id}
                />
              ))}
          </ApproverContainer>
          <AccordionSummary>
            <Typography className={classes.subtitle}>
              {t("slotDetails.caveats")}
            </Typography>
          </AccordionSummary>
          <ApproverContainer>
            {approvals
              .filter((approval) => !approval.memberName)
              .map((approval) => (
                <CaveatCard
                  caveat={approval.resalvas ?? undefined}
                  key={approval.id}
                />
              ))}
          </ApproverContainer>
        </CustomAccordion>
      )}
    </MinuteInfoContainer>
  );
};

export default MinuteDeliberationInfo;

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.typography.h5,
  },
  subtitle: {
    font: "Inter",
    fontWeight: "bold",
    fontSize: "14.52px",
    color: "#4460BF",
    lineHeight: "17.57px",
  },
}));
